/**
 * predictive-search.js — <predictive-search> wraps the search form inside the search dialog.
 * Fetches routes.predictive_search_url with section_id=predictive-search (server-rendered by
 * sections/predictive-search.liquid) and swaps the HTML into [data-search-results].
 * Keyboard: ArrowDown from the input moves into the results; ArrowUp/Down move between results;
 * ArrowUp on the first result returns to the input; Enter follows the focused link; Escape is
 * handled by the dialog's focus trap (header.js). Result counts are announced via the live region.
 * Only loaded when settings.predictive_search_enabled; the form submits normally without it.
 */
import { debounce, announce, define } from './theme.js';

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.popular = this.querySelector('[data-search-popular]');
    if (!this.form || !this.input || !this.results) return;

    this.url = this.dataset.url || '/search/suggest';
    this.sectionId = this.dataset.section || 'predictive-search';
    this.limit = Math.min(10, Math.max(1, Number(this.dataset.limit) || 6));
    this.controller = null;
    this.lastQuery = '';

    this.input.addEventListener('input', debounce(() => this.search(), 250));
    this.form.addEventListener('submit', (event) => { if (!this.input.value.trim()) event.preventDefault(); });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  get focusables() {
    return Array.from(this.results.querySelectorAll('a[href], button:not([disabled])'));
  }

  async search() {
    const query = this.input.value.trim();
    this.controller?.abort();
    if (!query) { this.reset(); return; }
    if (query === this.lastQuery) return;

    this.controller = new AbortController();
    this.setAttribute('loading', '');
    const url = new URL(this.url, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', String(this.limit));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(url.toString(), { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const html = await response.text();
      if (this.input.value.trim() !== query) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.getElementById(`shopify-section-${this.sectionId}`) || doc.body;
      this.results.innerHTML = section.innerHTML;
      this.results.hidden = false;
      if (this.popular) this.popular.hidden = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.lastQuery = query;
      this.announceCount(query);
    } catch (error) {
      if (error.name !== 'AbortError') this.reset();
    } finally {
      this.removeAttribute('loading');
    }
  }

  announceCount(query) {
    const count = Number(this.results.querySelector('[data-results-count]')?.dataset.resultsCount ?? 0);
    const { announceOne, announceOther, announceNone } = this.dataset;
    if (count === 0) announce((announceNone || '').replace('[terms]', query));
    else announce((count === 1 ? announceOne : announceOther || '').replace('[count]', String(count)));
  }

  reset() {
    this.results.innerHTML = '';
    this.results.hidden = true;
    if (this.popular) this.popular.hidden = false;
    this.input.setAttribute('aria-expanded', 'false');
    this.lastQuery = '';
  }

  onKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const list = this.focusables;
    if (!list.length || this.results.hidden) return;
    const active = document.activeElement;
    const index = list.indexOf(active);

    if (active === this.input) {
      if (event.key === 'ArrowDown') { event.preventDefault(); list[0].focus(); }
      return;
    }
    if (index === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowDown') list[Math.min(index + 1, list.length - 1)].focus();
    else if (index === 0) this.input.focus();
    else list[index - 1].focus();
  }
}

define('predictive-search', PredictiveSearch);
